"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.enrichBrokenAlertsInfo = exports.getBrokenAlertFiltersBasicInfo = exports.isBrokenAlertAttributeFilterInfo = exports.isBrokenAlertDateFilterInfo = void 0;
var tslib_1 = require("tslib");
// (C) 2021 GoodData Corporation
var sdk_backend_spi_1 = require("@gooddata/sdk-backend-spi");
var sdk_model_1 = require("@gooddata/sdk-model");
var last_1 = tslib_1.__importDefault(require("lodash/last"));
var partition_1 = tslib_1.__importDefault(require("lodash/partition"));
var filters_1 = require("../../utils/filters");
var translationUtils_1 = require("./translationUtils");
function isBrokenAlertDateFilterInfo(item) {
    return sdk_backend_spi_1.isDashboardDateFilter(item.alertFilter);
}
exports.isBrokenAlertDateFilterInfo = isBrokenAlertDateFilterInfo;
function isBrokenAlertAttributeFilterInfo(item) {
    return sdk_backend_spi_1.isDashboardAttributeFilter(item.alertFilter);
}
exports.isBrokenAlertAttributeFilterInfo = isBrokenAlertAttributeFilterInfo;
/**
 * Gets the information about the so called broken alert filters. These are filters that are set up on the alert,
 * but the currently applied filters either do not contain them, or the KPI has started ignoring them
 * since the alert was first set up.
 *
 * @param alert - the alert to compute the broken filters for
 * @param kpi - the KPI widget that the alert is relevant to
 * @param appliedFilters - all the currently applied filters (including All Time date filters)
 */
function getBrokenAlertFiltersBasicInfo(alert, kpi, appliedFilters) {
    var _a, _b;
    var alertFilters = (_a = alert === null || alert === void 0 ? void 0 : alert.filterContext) === null || _a === void 0 ? void 0 : _a.filters;
    // no filters -> no filters can be broken, bail early
    if (!alertFilters) {
        return [];
    }
    var result = [];
    var _c = partition_1.default(alertFilters, sdk_backend_spi_1.isDashboardDateFilter), alertDateFilters = _c[0], alertAttributeFilters = _c[1];
    // attribute filters
    var appliedAttributeFilters = appliedFilters.filter(sdk_model_1.isAttributeFilter);
    alertAttributeFilters.forEach(function (alertFilter) {
        // ignored attribute filters are broken even if they are noop
        var isIgnored = filters_1.isAttributeFilterIgnored(kpi, alertFilter.attributeFilter.displayForm);
        if (isIgnored) {
            result.push({
                alertFilter: alertFilter,
                brokenType: "ignored",
            });
            return;
        }
        // deleted attribute filters are broken even if they are noop
        var isInAppliedFilters = appliedAttributeFilters.some(function (f) {
            return sdk_model_1.areObjRefsEqual(sdk_model_1.filterObjRef(f), alertFilter.attributeFilter.displayForm);
        });
        var isDeleted = !isInAppliedFilters;
        if (isDeleted) {
            result.push({
                alertFilter: alertFilter,
                brokenType: "deleted",
            });
        }
    });
    // date filter
    var alertDateFilter = last_1.default(alertDateFilters);
    if (alertDateFilter) {
        var isIrrelevantNow = filters_1.isDateFilterIrrelevant(kpi);
        if (isIrrelevantNow) {
            result.push({
                alertFilter: {
                    dateFilter: tslib_1.__assign(tslib_1.__assign({}, alertDateFilter.dateFilter), { dataSet: (_b = alertDateFilter.dateFilter.dataSet) !== null && _b !== void 0 ? _b : kpi.dateDataSet }),
                },
                brokenType: "ignored",
            });
        }
    }
    return result;
}
exports.getBrokenAlertFiltersBasicInfo = getBrokenAlertFiltersBasicInfo;
/**
 * Takes basic broken alert info and adds additional information used for displaying of such filters to the user.
 *
 * @param brokenAlertFilters - the basic broken alert filters info to enrich
 * @param intl - the intl object used
 * @param dateFormat - the date format to be used
 * @param dateDataSets - all available date data sets
 * @param attributeFiltersMeta - additional information about attribute filters (see {@link IAttributeFilterMetaCollection} for details)
 */
function enrichBrokenAlertsInfo(brokenAlertFilters, intl, dateFormat, dateDataSets, attributeFiltersMeta) {
    return brokenAlertFilters.map(function (brokenFilter) {
        if (isBrokenAlertAttributeFilterInfo(brokenFilter)) {
            return enrichBrokenAttributeFilter(brokenFilter, attributeFiltersMeta);
        }
        if (isBrokenAlertDateFilterInfo(brokenFilter)) {
            return enrichBrokenDateFilter(brokenFilter, intl, dateFormat, dateDataSets);
        }
        throw new sdk_backend_spi_1.UnexpectedError("Unknown broken alert filter type.");
    });
}
exports.enrichBrokenAlertsInfo = enrichBrokenAlertsInfo;
function enrichBrokenDateFilter(brokenFilter, intl, dateFormat, dateDataSets) {
    var _a;
    var alertFilter = brokenFilter.alertFilter, brokenType = brokenFilter.brokenType;
    var dateFilterTitle = translationUtils_1.translateDateFilter(alertFilter, intl, dateFormat);
    var matchingDateDataset = dateDataSets.find(function (dataset) {
        return sdk_model_1.areObjRefsEqual(dataset, alertFilter.dateFilter.dataSet);
    });
    return {
        type: "date",
        brokenType: brokenType,
        dateFilterTitle: dateFilterTitle,
        title: (_a = matchingDateDataset === null || matchingDateDataset === void 0 ? void 0 : matchingDateDataset.title) !== null && _a !== void 0 ? _a : intl.formatMessage({ id: "kpiAlertDialog.brokenAlertDefaultDateLabel" }),
    };
}
function enrichBrokenAttributeFilter(brokenFilter, attributeFiltersMeta) {
    var alertFilter = brokenFilter.alertFilter, brokenType = brokenFilter.brokenType;
    var metaKey = sdk_model_1.objRefToString(alertFilter.attributeFilter.displayForm);
    var meta = attributeFiltersMeta[metaKey];
    var isNegative = alertFilter.attributeFilter.negativeSelection;
    var totalCount = meta.totalElementsCount;
    var elements = meta.validElements.filter(function (element) {
        var isInSelected = sdk_model_1.isAttributeElementsByRef(alertFilter.attributeFilter.attributeElements)
            ? alertFilter.attributeFilter.attributeElements.uris.some(function (uri) { return uri === element.uri; })
            : alertFilter.attributeFilter.attributeElements.values.some(function (value) { return value === element.title; });
        return isInSelected !== isNegative;
    });
    var selection = elements.map(function (el) { return el.title; }).join(", ");
    var title = meta.title;
    var selectedCount = sdk_model_1.attributeElementsCount(alertFilter.attributeFilter.attributeElements);
    return {
        type: "attribute",
        brokenType: brokenType,
        isAllSelected: isNegative && sdk_model_1.attributeElementsIsEmpty(alertFilter.attributeFilter.attributeElements),
        selection: selection,
        selectionSize: isNegative ? totalCount - selectedCount : selectedCount,
        title: title,
    };
}
//# sourceMappingURL=brokenFilterUtils.js.map